import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../../hooks/useAuth';
import { useFirestoreQuery } from '../../../../hooks/useFirestoreQuery';
import { useSaldo } from '../../../../hooks/useSaldo'; // ✅ NOVO HOOK
import { ITransacao as Transacao } from '../../../../types/firestore.types';
import { PROMOCAO } from '../../../../constants/promocao';
import { formatarMoeda as formatCurrency } from '../../../../utils/currencyUtils';
import Card from '../../../../components/ui/Card';
import Spinner from '../../../../components/ui/Spinner';

interface BalanceCardProps { }

interface TransacaoCorrigida extends Transacao {
  tipo_operoso?: string;
  valor_total?: number;
  cashback_percentual?: number;
  cashback_valor?: number;
  transacao_origem?: string;
  promocao_id?: string;
}

const BalanceCard: React.FC<BalanceCardProps> = () => {
  const { usuario } = useAuth();
  const clienteId = usuario?.id;
  const [showDetails, setShowDetails] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<string>('');
  // Saldos dos dois bolsos, calculados no servidor (mesma regra do caixa).
  const [carteira, setCarteira] = useState<{
    promocao21: number;
    validandoPromocao21: number;
  } | null>(null);

  const {
    dados: transacoes,
    carregando,
    erro,
  } = useFirestoreQuery<Transacao>(
    'transacoes',
    clienteId ? [{ campo: 'userId', operador: '==', valor: clienteId }] : null,
    null,
    0,
    ['transacoesCliente', clienteId]
  );

  // ✅ USANDO O NOVO HOOK PARA CÁLCULO CONSISTENTE
  const { saldoDisponivel, saldoValidando, saldoTotal } = useSaldo(transacoes || []);

  // **FUNÇÃO PARA VERIFICAR SE PASSARAM 48 HORAS** (mantida para estatísticas)
  const isMaisDe48Horas = (dataTransacao: Date): boolean => {
    const agora = new Date();
    const diferencaMs = agora.getTime() - dataTransacao.getTime();
    const diferencaHoras = diferencaMs / (1000 * 60 * 60);
    return diferencaHoras >= 48;
  };

  // **SALDO DA PROMOÇÃO DE 21 ANOS (vem do servidor)**
  // A separação entre o bolso da promoção e o geral obedece às mesmas regras
  // do caixa, então o cálculo é a callable consultarCarteira.
  const buscarCarteira = useCallback(async () => {
    if (!clienteId) return;
    try {
      const resposta = await httpsCallable<Record<string, never>, {
        success: boolean;
        promocao21?: number;
        validandoPromocao21?: number;
      }>(getFunctions(), 'consultarCarteira')({});
      if (resposta.data.success) {
        setCarteira({
          promocao21: resposta.data.promocao21 || 0,
          validandoPromocao21: resposta.data.validandoPromocao21 || 0
        });
      }
    } catch {
      // Função ainda não publicada ou fora do ar: o card segue sem essa linha.
    }
  }, [clienteId]);

  useEffect(() => {
    buscarCarteira();
  }, [buscarCarteira]);

  // **DEBUG: Análise detalhada das transações**
  useEffect(() => {
    if (transacoes && transacoes.length > 0) {

      const transacoesPix = transacoes.filter(t => t.tipo === 'pix_payment');
      const transacoesCashback = transacoes.filter(t => t.tipo === 'cashback');
      const transacoesValidando = transacoes.filter(t =>
        t.status === 'validating' || t.status === 'pending' || t.status === 'pendente'
      );

      transacoesPix.forEach((transacao, index) => {
        const t = transacao as TransacaoCorrigida;
        let dataInfo = 'Data não disponível';
        if (t.createdAt) {
          try {
            const data = t.createdAt.toDate();
            dataInfo = data.toLocaleString('pt-BR');
            const horasAtras = (new Date().getTime() - data.getTime()) / (1000 * 60 * 60);
            dataInfo += ` (${horasAtras.toFixed(1)} horas atrás)`;
          } catch (error) {
            dataInfo = 'Erro na data';
          }
        }

      });

    }
  }, [transacoes]);

  useEffect(() => {
    if (transacoes && transacoes.length > 0) {
      const agora = new Date();
      setUltimaAtualizacao(agora.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));

      setPulse(true);
      const timer = setTimeout(() => setPulse(false), 600);

      return () => clearTimeout(timer);
    }
  }, [transacoes, saldoDisponivel, saldoValidando]);

  // **ESTATÍSTICAS ATUALIZADAS**
  const estatisticas = useMemo(() => {
    if (!transacoes) return null;

    const estatisticas = {
      totalTransacoes: transacoes.length,
      transacoesDisponiveis: 0,
      transacoesValidando: 0,
      transacoesPendentes: 0,
      transacoesFalhas: 0,
      transacoesPix: 0,
      transacoesCashback: 0,
      valorPixValidando: 0,
      valorCashbackValidando: 0,
      valorPixDisponivel: 0,
      valorCashbackDisponivel: 0,
      valorPromocao21Validando: 0,
      transacoesAguardando48h: 0,
      valorAguardando48h: 0,
      ultimaTransacao: null as Transacao | null
    };

    transacoes.forEach(transacao => {
      const t = transacao as TransacaoCorrigida;
      const tipo_operacao = t.tipo_operacao || t.tipo_operoso;
      const status = t.status;
      const tipo = t.tipo;

      // A promoção de 21 anos tem dois créditos (PIX + bônus) que ficam em
      // validação juntos: o cliente precisa ver esse total separado do resto,
      // igual à divisão em dois bolsos que o servidor faz no pagamento.
      const ehPromocao21 = t.promocao_id === PROMOCAO.id;

      let valor = t.valor || 0;

      let multiplicador = 1;
      if (tipo_operacao === 'debito') {
        multiplicador = -1;
      }

      const valorCalculado = valor * multiplicador;

      // Verificar data para regra das 48 horas
      let dataTransacao: Date | null = null;
      if (t.createdAt) {
        try {
          dataTransacao = t.createdAt.toDate();
        } catch (error) {
          console.warn('Erro ao processar data da transação:', t.id);
        }
      }

      const passou48Horas = dataTransacao ? isMaisDe48Horas(dataTransacao) : false;

      // Contagem por tipo de transação
      if (tipo === 'pix_payment') {
        estatisticas.transacoesPix++;
        if ((status === 'completed' || status === 'confirmed') && passou48Horas) {
          estatisticas.valorPixDisponivel += valorCalculado;
        } else if (status === 'validating' || status === 'pending' ||
          (status === 'completed' && !passou48Horas)) {
          if (ehPromocao21) estatisticas.valorPromocao21Validando += valorCalculado;
          else estatisticas.valorPixValidando += valorCalculado;
        }
      } else if (tipo === 'cashback') {
        estatisticas.transacoesCashback++;
        if ((status === 'completed' || status === 'confirmed') && passou48Horas) {
          estatisticas.valorCashbackDisponivel += valorCalculado;
        } else if (status === 'validating' || status === 'pending' ||
          (status === 'completed' && !passou48Horas)) {
          if (ehPromocao21) estatisticas.valorPromocao21Validando += valorCalculado;
          else estatisticas.valorCashbackValidando += valorCalculado;
        }
      }

      // Contagem por status com regra das 48 horas
      if ((status === 'completed' || status === 'confirmed') && passou48Horas) {
        estatisticas.transacoesDisponiveis++;
      } else if (status === 'validating' || status === 'pending' ||
        (status === 'completed' && tipo_operacao !== 'debito' && !passou48Horas)) {
        estatisticas.transacoesValidando++;

        // Contar transações aguardando 48 horas
        if (status === 'completed' && !passou48Horas) {
          estatisticas.transacoesAguardando48h++;
          estatisticas.valorAguardando48h += valorCalculado;
        }
      } else if (status === 'failed' || status === 'cancelled' || status === 'expired') {
        estatisticas.transacoesFalhas++;
      }

      // Última transação
      if (t.createdAt) {
        try {
          const dataTransacao = t.createdAt.toDate();
          if (!estatisticas.ultimaTransacao ||
            dataTransacao.getTime() > estatisticas.ultimaTransacao.createdAt.toDate().getTime()) {
            estatisticas.ultimaTransacao = transacao;
          }
        } catch (error) {
          console.warn('Erro ao processar data da transação:', t.id);
        }
      }
    });

    return estatisticas;
  }, [transacoes]);

  // O servidor é a fonte da verdade da promoção; o cálculo local fica como
  // reserva enquanto a callable não respondeu.
  const validandoPromocao21 = carteira
    ? carteira.validandoPromocao21
    : (estatisticas?.valorPromocao21Validando || 0);

  const formatarDataUltimaTransacao = () => {
    if (!estatisticas?.ultimaTransacao?.createdAt) return 'Nenhuma transação';

    try {
      const data = estatisticas.ultimaTransacao.createdAt.toDate();
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    hover: {
      y: -5,
      transition: { duration: 0.3 }
    },
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  if (!usuario) {
    return (
      <Card className="p-8 bg-gradient-to-r from-[#c5a663] mr-6 via-[#c0b067] to-[#c5a663] border-primary/20 text-white rounded-2xl shadow-2xl flex items-center justify-center min-h-[220px] backdrop-blur-sm bg-opacity-90 border">
        <p className="text-lg text-gray-300">Faça login para ver seu saldo.</p>
      </Card>
    );
  }

  if (carregando) {
    return (
      <Card className="p-8 bg-gradient-to-r from-[#c5a663] mr-6 via-[#c0b067] to-[#c5a663] text-white rounded-2xl shadow-2xl flex flex-col items-center justify-center min-h-[220px] backdrop-blur-sm bg-opacity-90 border border-gray-700">
        <div className="flex items-center">
          <Spinner size="lg" color="white" />
          <p className="ml-4 text-lg text-gray-200">Carregando saldo...</p>
        </div>
        <p className="text-sm text-gray-400 mt-2">Isso pode levar alguns instantes</p>
      </Card>
    );
  }

  if (erro) {
    return (
      <Card className="p-8 bg-gradient-to-br from-red-900 to-red-800 text-white rounded-2xl shadow-2xl flex flex-col items-center justify-center min-h-[220px] backdrop-blur-sm bg-opacity-90 border border-red-700">
        <div className="mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-lg text-center">Erro ao carregar saldo</p>
        <p className="text-sm text-red-200 mt-2 text-center">{erro.message}</p>
      </Card>
    );
  }

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="relative overflow-hidden rounded-2xl shadow-2xl p-8 bg-primary/1 text-white transform transition-all duration-300 ease-out backdrop-blur-sm bg-opacity-90 border border-primary/20"
    >
      {/* Efeito de partículas de fundo */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 10 + 2}px`,
              height: `${Math.random() * 10 + 2}px`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0, 0.7, 0],
            }}
            transition={{
              duration: Math.random() * 5 + 5,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* Padrão de gradiente animado */}
      <div className="absolute inset-0 opacity-10 bg-primary animate-shimmer" />

      {/* Elementos geométricos de fundo */}
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white opacity-5"></div>
      <div className="absolute -left-10 -bottom-10 w-32 h-32 rounded-full bg-white opacity-5"></div>

      {/* IMAGEM DE FUNDO - MODIFICADA */}
      <motion.img
        src="/Coroa Tugas - PNG.png"
        alt="Saldo"
        className="absolute right-0 md:mb-10 mt-10 h-80 object-contain opacity-80 z-0"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      />

      <motion.div
        className="relative z-10"
        variants={contentVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="flex justify-between items-start mb-6">
          <div className="flex items-center">
            <div>
              <motion.img
                src="/saldo.png"
                alt="Saldo"
                className="w-full object-contain flex-1 justify-self-end z-10"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              />
              <p className="text-sm text-white text-opacity-60 mt-1">Disponível após 48h de validação</p>
            </div>
          </div>

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowDetails(!showDetails)}
            className="bg-white bg-opacity-10 hover:bg-opacity-20 p-2 rounded-xl transition-all duration-200 backdrop-blur z-20"
            aria-label={showDetails ? "Ocultar detalhes" : "Mostrar detalhes"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {showDetails ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              )}
            </svg>
          </motion.button>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-2 z-10">
          {/* Saldo Disponível */}
          <div className="mb-4">
            <p className="text-sm text-white mb-1 z-10">Saldo Disponível</p>
            <motion.p
              className={`text-5xl md:text-6xl font-bold text-white ${pulse ? 'animate-pulse' : ''} z-10`}
              key={saldoDisponivel}
              initial={{ scale: 0.9, opacity: 0.5 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {formatCurrency(saldoDisponivel).replace('R$', 'TC')}
            </motion.p>
            <div className="flex items-center">
              <div className="w-4 h-4 text-sm bg-primary/70 rounded-full flex items-center justify-center mr-3">
                <span className="text-black m-2">✓</span>
              </div>
              <span className="text-primary text-sm">Disponível para uso imediato</span>
            </div>
          </div>

          {/* Saldo Total */}
          <div className="border-t border-white border-opacity-20 pt-4 mt-2 w-[50%]">
            <p className="text-sm text-white mb-1 z-10">Saldo Total</p>
            <p className="text-2xl font-bold text-white">
              {formatCurrency(saldoTotal).replace('R$', 'TC')}
            </p>
            <div className="flex items-center">
              <div className="w-4 h-4 text-xs bg-gray-300/70 rounded-full flex items-center justify-center mr-3">
                <span className="text-black m-2">✓</span>
              </div>
              <span className="text-white text-opacity-60 text-xs">Disponível + Em validação</span>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden z-10"
            >
              <div className="pt-4 border-t border-white border-opacity-20">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex flex-col">
                    <span className="text-white text-opacity-70">Total transações:</span>
                    <span className="font-medium text-lg">{(estatisticas?.totalTransacoes - estatisticas?.transacoesFalhas) || 0}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-white text-opacity-70">Em validação:</span>
                    <span className="font-medium text-yellow-300">{estatisticas?.transacoesValidando || 0}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-white text-opacity-70">Transações PIX:</span>
                    <span className="font-medium text-blue-300">{(estatisticas?.transacoesPix - estatisticas?.transacoesFalhas) || 0}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-white text-opacity-70">Transações Cashback:</span>
                    <span className="font-medium text-green-300">{estatisticas?.transacoesCashback || 0}</span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-white text-opacity-70">PIX em validação:</span>
                    <span className="font-medium text-yellow-300">
                      {formatCurrency(estatisticas?.valorPixValidando || 0).replace('R$', 'TC')}
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-white text-opacity-70">Cashback em validação:</span>
                    <span className="font-medium text-yellow-300">
                      +{formatCurrency(estatisticas?.valorCashbackValidando || 0).replace('R$', 'TC')}
                    </span>
                  </div>

                  <div className="flex flex-col col-span-2">
                    <span className="text-white text-opacity-70">Promoção 21 anos (disponível):</span>
                    <span className={`font-medium text-lg ${carteira?.promocao21 ? 'text-primary' : 'text-white text-opacity-50'}`}>
                      {formatCurrency(carteira?.promocao21 || 0).replace('R$', 'TC')}
                    </span>
                    <span className="text-xs text-white text-opacity-60">
                      Saldo desta promoção liberado para uso. Vale para consumo no local, retirada e evento na Cave.
                    </span>
                  </div>

                  <div className="flex flex-col col-span-2">
                    <span className="text-white text-opacity-70">Promoção 21 anos (em validação):</span>
                    <span className={`font-medium text-lg ${validandoPromocao21 ? 'text-primary' : 'text-white text-opacity-50'}`}>
                      +{formatCurrency(validandoPromocao21).replace('R$', 'TC')}
                    </span>
                    {!!validandoPromocao21 && (
                      <span className="text-xs text-white text-opacity-60">
                        Pague {formatCurrency(PROMOCAO.valorCompra).replace('R$', 'TC')} e leve{' '}
                        {formatCurrency(PROMOCAO.valorTotal).replace('R$', 'TC')}. Disponível após 48h.
                      </span>
                    )}
                  </div>

                  {/* Nova estatística: Aguardando 48h */}
                  <div className="flex flex-col col-span-2">
                    <span className="text-white text-opacity-70">Aguardando 48 horas:</span>
                    <span className="font-medium text-orange-300">
                      {formatCurrency(estatisticas?.valorAguardando48h || 0).replace('R$', 'TC')}
                    </span>
                    <span className="text-xs text-white text-opacity-60">
                      {estatisticas?.transacoesAguardando48h || 0} transações
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-white border-opacity-10">
                  <div className="flex justify-between text-sm">
                    <span className="text-white text-opacity-70">Última transação:</span>
                    <span className="font-medium">{formatarDataUltimaTransacao()}</span>
                  </div>

                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-white text-opacity-70">Atualizado em:</span>
                    <span className="font-medium">{ultimaAtualizacao || 'Agora'}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default BalanceCard;